import { MutableRefObject, useEffect, useRef, useState } from "react";
import { useWindowSize } from "./use-window-size";

interface InputArrayBase {
    id: string;
}

/** Checks if the content of an array overflows a container, if it does overflow set hidden values as true on elements in the array */
function useHideOverflowContent<T extends InputArrayBase>(inputArray: T[], containerRef: MutableRefObject<HTMLDivElement>): (T & { hidden: boolean })[] {
    const lastShownId = useRef<string>(null);
    const [inputArrayWithHiddenValue, setInputArrayWithHiddenValue] = useState(inputArray.map((element) => ({ ...element, hidden: false })));
    const { screenWidth } = useWindowSize();

    useEffect(() => {
        lastShownId.current = null;
    }, [screenWidth]);

    useEffect(() => {
        // Set input array with with its previous hidden value or false if it did not exist before
        setInputArrayWithHiddenValue(
            inputArray.map((element) => {
                const hidden = inputArrayWithHiddenValue.find(({ id }) => id === element.id)?.hidden ?? false;
                return { ...element, hidden };
            })
        );
        lastShownId.current = null;
    }, [JSON.stringify(inputArray)]);

    useEffect(() => {
        // If content overflows the container
        if (containerRef?.current?.clientWidth < containerRef?.current?.scrollWidth) {
            let hiddenBreadcrumb = false;
            // Hide the next showing element in the array
            setInputArrayWithHiddenValue(
                inputArrayWithHiddenValue.map((element, index) => {
                    // Only hide if element is showing and only hide one element at a time
                    if (!hiddenBreadcrumb && index !== 0 && !element.hidden) {
                        hiddenBreadcrumb = true;
                        return { ...element, hidden: true };
                    }
                    return element;
                })
            );
        } else {
            // If content does not over flow the container
            // Find the last hidden element
            const elementIdToShow = [...inputArrayWithHiddenValue].reverse().find(({ hidden }) => hidden)?.id;
            // If last hidden element is not the last shown one
            if (elementIdToShow !== lastShownId.current) {
                // Show the last hidden element
                setInputArrayWithHiddenValue(inputArrayWithHiddenValue.map((element) => ({ ...element, hidden: element.id === elementIdToShow ? false : element.hidden })));
                lastShownId.current = elementIdToShow;
            }
        }
    }, [JSON.stringify(inputArrayWithHiddenValue), screenWidth]);

    return inputArrayWithHiddenValue;
}
export default useHideOverflowContent;
